import React, { useState, useEffect, useCallback } from "react";
import { useNetwork } from "../NetworkProvider";
import { TextField, InputAdornment } from "@mui/material";
import { HiMagnifyingGlass } from "react-icons/hi2";

import FastAPIClient from "../../client";
import config from "../../config";
const client = new FastAPIClient(config);
// import { useNavigate } from "react-router-dom"; // Make sure you import useNavigate
import { FormattedMessage } from "react-intl";
import SearchLoadSkeleton from "../../components/Skeletons/SearchLoadSkeleton";

const ProductSearch = ({
    allProducts,
    handleClick,
    selectedProduct,
    setFilteredOrders,
}) => {
    const { isOnline } = useNetwork();
    const [productList, setProductList] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    // Add debounce delay constant
    const DEBOUNCE_DELAY = 300; // 300ms delay

    useEffect(() => {
        setProductList(allProducts);
    }, [allProducts]);

    // Create a debounced search function using useCallback
    const debouncedSearch = useCallback(
        async (term) => {
            if (term === "") {
                setShowSuggestions(false);
                setFilteredProducts([]);
                setLoading(false);
                return;
            }

            setLoading(true);

            if (!isOnline || productList.length > 0) {
                const filtered = productList.filter(
                    (product) =>
                        (product.short_label || product.product_name)
                            .toLowerCase()
                            .includes(term.toLowerCase()) ||
                        product.product_id.toString().includes(term)
                );
                setFilteredOrders(filtered);
                setFilteredProducts(filtered);
                setLoading(false);
            } else {
                try {
                    const filtered = await client.getAllProducts({
                        search: term,
                    });
                    setFilteredProducts(filtered?.results);
                } catch (error) {
                    console.error("Search error:", error);
                    setFilteredProducts([]);
                } finally {
                    setLoading(false);
                }
            }
        },
        [isOnline, productList]
    );

    // Replace the search term effect with a debounced version
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (searchTerm !== "") {
                setHasSearched(true);
                debouncedSearch(searchTerm);
            } else {
                setHasSearched(false);
                if (setFilteredOrders) {
                    setFilteredOrders(allProducts);
                }
            }
        }, DEBOUNCE_DELAY);

        // Cleanup function to cancel the timeout if the component unmounts
        // or if searchTerm changes before the delay is finished
        return () => clearTimeout(debounceTimer);
    }, [searchTerm, debouncedSearch]);

    const toggleShowSuggestions = (arg) => {
        if (!handleClick) {
            setShowSuggestions(false);
        } else {
            setShowSuggestions(arg);
        }
    };

    useEffect(() => {
        toggleShowSuggestions(false);
    }, [selectedProduct]);

    const handleChange = (e) => {
        setSearchTerm(e.target.value);
        if (handleClick) {
            setShowSuggestions(true);
        }
    };

    return (
        <div className="flex flex-col md:flex-row items-center w-full">
            <div className="w-full md:pr-5 relative">
                <FormattedMessage
                    id="product.searchplaceholder"
                    defaultMessage="Rechercher un produit ..."
                >
                    {(placeholder) => (
                        <TextField
                            type="text"
                            fullWidth={true}
                            className="mt-1 block"
                            sx={{
                                minWidth: 250,
                                "& .MuiInputBase-root": {
                                    backgroundColor: "#f3f4f6",
                                    borderRadius: "9999px",
                                    height: "40px",
                                    "@media (max-width: 768px)": {
                                        height: "48px", // Taller input on mobile
                                    },
                                    "&:hover": {
                                        backgroundColor: "#f3f4f6",
                                    },
                                    "&.Mui-focused": {
                                        backgroundColor: "#f3f4f6",
                                    },
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                                "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                                    {
                                        border: "none",
                                    },
                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                        border: "none",
                                    },
                                "& .MuiInputBase-input": {
                                    padding: "8px 14px",
                                    fontSize: "0.875rem",
                                    "@media (max-width: 768px)": {
                                        fontSize: "1.125rem", // 18px text on mobile
                                        padding: "12px 14px", // More padding on mobile
                                    },
                                },
                                "& .MuiInputAdornment-root": {
                                    "@media (max-width: 768px)": {
                                        marginLeft: "12px", // More space for the search icon on mobile
                                    },
                                },
                            }}
                            placeholder={placeholder}
                            value={searchTerm}
                            onChange={handleChange}
                            onBlur={() => toggleShowSuggestions(false)}
                            onFocus={() => toggleShowSuggestions(true)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <HiMagnifyingGlass
                                            className="text-gray-500"
                                            size={18}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                </FormattedMessage>
                {showSuggestions && searchTerm !== "" && (
                    <div className="absolute top-full w-full rounded-lg max-h-60 z-10 mt-1 pr-5 ">
                        <div className="w-full max-h-60 bg-white border border-slate-300 rounded-lg shadow-lg overflow-y-auto">
                            {loading ? (
                                <SearchLoadSkeleton />
                            ) : filteredProducts.length > 0 ? (
                                filteredProducts.map((product) => (
                                    <div
                                        key={product.product_id}
                                        onMouseDown={(e) => {
                                            e.preventDefault(); // Prevent any unwanted behavior
                                            handleClick(product);
                                        }}
                                        className="cursor-pointer px-3 py-2 hover:bg-slate-100 w-full"
                                    >
                                        <div className="flex flex-row items-center border-b border-gray-100 text-left">
                                            <div
                                                className={
                                                    product.short_label
                                                        ? "w-1/2"
                                                        : "w-5/6"
                                                }
                                            >
                                                {product.short_label ||
                                                    product.product_name}
                                            </div>
                                            <div className="text-gray-500 text-sm ml-2 w-1/6">
                                                {product.product_id}
                                            </div>
                                            <div
                                                className={`text-gray-500 text-sm ml-2 w-1/3 ${
                                                    product.packaging_code
                                                        ? "w-1/3"
                                                        : "w-0"
                                                }`}
                                            >
                                                {product.packaging_code}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : hasSearched && searchTerm !== "" ? (
                                <div className="px-3 py-2">
                                    <FormattedMessage id="no-match" />
                                </div>
                            ) : null}
                        </div>
                    </div>
                )}
            </div>

            {/* Show suggestions if any clients match the search term */}
        </div>
    );
};

export default ProductSearch;
